import PropTypes from "prop-types";
import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateHaafFinancialYear,
} from "@/api/haaf-payments";
import { getFinancialYearId } from "./use-selected-haaf-financial-year";

const CREATE_FY_VALUE = "__create_financial_year__";

function getCurrentFinancialStartYear() {
  const now = new Date();
  const year = now.getFullYear();
  return now.getMonth() >= 3 ? year : year - 1;
}

export function FinancialYearSelector({
  financialYears,
  selectedFinancialYearId,
  onChange,
}) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [startYear, setStartYear] = useState(String(getCurrentFinancialStartYear()));
  const createMutation = useCreateHaafFinancialYear();

  const handleValueChange = (value) => {
    if (value === CREATE_FY_VALUE) {
      setIsCreateOpen(true);
      return;
    }

    onChange(value);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const created = await createMutation.mutateAsync({
      startYear: Number(startYear),
      isActive: true,
    });
    const createdId = getFinancialYearId(created);
    if (createdId) {
      onChange(createdId);
    }
    setIsCreateOpen(false);
  };

  return (
    <>
      <Select value={selectedFinancialYearId} onValueChange={handleValueChange}>
        <SelectTrigger className="h-9 w-full md:w-[190px]">
          <SelectValue placeholder="Select FY" />
        </SelectTrigger>
        <SelectContent>
          {financialYears
            .map((year) => ({ ...year, optionId: getFinancialYearId(year) }))
            .filter((year) => year.optionId)
            .map((year) => (
              <SelectItem key={year.optionId} value={year.optionId}>
                {year.name || `${year.startYear}-${year.endYear}`}{" "}
                {year.isActive ? "(Active)" : ""}
              </SelectItem>
            ))}
          <SelectSeparator />
          <SelectItem value={CREATE_FY_VALUE}>
            <span className="flex items-center gap-2">
              <Plus className="h-3.5 w-3.5" />
              Add Financial Year
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Financial Year</DialogTitle>
            <DialogDescription>
              Enter the FY start year. Example: 2026 creates 2026-27.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="haf-start-year">Start Year</Label>
              <Input
                id="haf-start-year"
                type="number"
                min="2000"
                max="2100"
                value={startYear}
                onChange={(event) => setStartYear(event.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={createMutation.isPending || createMutation.isLoading}
            >
              Create FY
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

FinancialYearSelector.propTypes = {
  financialYears: PropTypes.array.isRequired,
  selectedFinancialYearId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
